import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  InfoOutlined as InfoOutlinedIcon,
  TuneOutlined as TuneOutlinedIcon,
} from '@mui/icons-material';
import { useSessionStore } from '../store/sessionStore';
import { MessageListWithStreaming } from '../components/sessions/MessageListWithStreaming';
import { MessageInput } from '../components/sessions/MessageInput';
import { ContextUsageIndicator } from '../components/sessions/ContextUsageIndicator';
import { ContextSummaryList } from '../components/sessions/ContextSummaryList';
import { SessionMemoryList } from '../components/sessions/SessionMemoryList';
import { FileUpload } from '../components/files/FileUpload';
import { FileList } from '../components/files/FileList';
import { SessionStatus } from '../types/session';
import { FileCategory } from '../types/file';
import { fileService } from '../services/fileService';
import { agentService } from '../services/agentService';
import { useNotificationStore } from '../store/notificationStore';

/**
 * Session detail page with chat interface.
 */
export const SessionDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    currentSession,
    messages,
    loading,
    error,
    loadSession,
    loadMessages,
    sendMessage,
    deleteSession,
    pauseSession,
    resumeSession,
    clearError,
  } = useSessionStore();
  const { showNotification } = useNotificationStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filesRefreshKey, setFilesRefreshKey] = useState(0);
  const [parameterLabelsByKey, setParameterLabelsByKey] = useState<Record<string, string>>({});

  useEffect(() => {
    if (id) {
      loadSession(id);
      loadMessages(id);
    }
  }, [id, loadSession, loadMessages]);

  useEffect(() => {
    const defId = currentSession?.agentDefinitionId;
    if (!defId) {
      setParameterLabelsByKey({});
      return;
    }
    let cancelled = false;
    void agentService
      .getAgent(defId)
      .then((agent) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const p of agent.parameters) {
          map[p.key] = p.label;
        }
        setParameterLabelsByKey(map);
      })
      .catch(() => {
        if (!cancelled) setParameterLabelsByKey({});
      });
    return () => {
      cancelled = true;
    };
  }, [currentSession?.agentDefinitionId]);

  const handleStreamingComplete = useCallback(() => {
    if (id) {
      loadMessages(id);
    }
  }, [id, loadMessages]);

  const parsedParameters = useMemo(() => {
    if (!currentSession?.initialParametersJson) return null;
    try {
      const params = JSON.parse(currentSession.initialParametersJson) as Record<string, unknown>;
      const entries = Object.entries(params);
      return entries.length > 0 ? entries : null;
    } catch {
      return null;
    }
  }, [currentSession?.initialParametersJson]);

  const handleSendMessage = async (payload: { content: string; file?: File; mediaType?: string }) => {
    if (!id) return;

    try {
      let fileId: string | undefined;

      if (payload.file) {
        const uploaded = await fileService.uploadFile(id, payload.file, FileCategory.Attachment);
        fileId = uploaded.id;
        setFilesRefreshKey((k) => k + 1);
      }

      await sendMessage(id, {
        content: payload.content,
        fileId,
        mediaType: payload.mediaType,
      });
      // AI response will arrive via SignalR streaming -- no manual reload needed
    } catch (err: any) {
      showNotification(err.message || 'Failed to send message', 'error');
    }
  };

  const handleDelete = async () => {
    if (!id || !currentSession) return;

    if (!window.confirm('Are you sure you want to delete this session?')) {
      return;
    }

    try {
      await deleteSession(id);
      showNotification('Session deleted successfully', 'success');
      navigate('/sessions');
    } catch (err: any) {
      showNotification(err.message || 'Failed to delete session', 'error');
    }
  };

  const handlePause = async () => {
    if (!id) return;

    try {
      await pauseSession(id);
      showNotification('Session paused', 'success');
    } catch (err: any) {
      showNotification(err.message || 'Failed to pause session', 'error');
    }
  };

  const handleResume = async () => {
    if (!id) return;

    try {
      await resumeSession(id);
      showNotification('Session resumed', 'success');
    } catch (err: any) {
      showNotification(err.message || 'Failed to resume session', 'error');
    }
  };

  const getStatusColor = (status: SessionStatus) => {
    switch (status) {
      case SessionStatus.Active:
        return 'success';
      case SessionStatus.Paused:
        return 'warning';
      case SessionStatus.Completed:
        return 'info';
      default:
        return 'error';
    }
  };

  const getStatusLabel = (status: SessionStatus) => {
    switch (status) {
      case SessionStatus.Active:
        return 'Active';
      case SessionStatus.Paused:
        return 'Paused';
      case SessionStatus.Completed:
        return 'Completed';
      case SessionStatus.Cancelled:
        return 'Cancelled';
      case SessionStatus.Expired:
        return 'Expired';
      case SessionStatus.Error:
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  if (loading && !currentSession) {
    return (
      <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentSession) {
    return (
      <Box sx={{ p: 6 }}>
        <Alert severity="error">Session not found</Alert>
      </Box>
    );
  }

  const sessionMessages = messages.get(id || '') || [];

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Card sx={{ borderRadius: 0, boxShadow: 2 }}>
        <Box
          sx={{
            p: 3,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/sessions')} variant="outlined" size="small">
            Back
          </Button>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {currentSession.sessionName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {currentSession.agentName}
            </Typography>
          </Box>
          <Chip
            label={getStatusLabel(currentSession.status)}
            color={getStatusColor(currentSession.status) as any}
            size="small"
          />
          <Tooltip title="Context details">
            <IconButton onClick={() => setSidebarOpen((v) => !v)}>
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
          {currentSession.status === SessionStatus.Active ? (
            <Tooltip title="Pause">
              <IconButton onClick={handlePause} color="warning">
                <PauseIcon />
              </IconButton>
            </Tooltip>
          ) : currentSession.status === SessionStatus.Paused ? (
            <Tooltip title="Resume">
              <IconButton onClick={handleResume} color="success">
                <PlayArrowIcon />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Delete">
            <IconButton onClick={handleDelete} color="error">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Card>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Chat Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {error && (
            <Alert severity="error" onClose={clearError} sx={{ m: 3 }}>
              {error}
            </Alert>
          )}

          <MessageListWithStreaming
            messages={sessionMessages}
            sessionId={id || ''}
            loading={loading}
            onStreamingComplete={handleStreamingComplete}
          />

          <MessageInput
            onSend={handleSendMessage}
            disabled={currentSession.status !== SessionStatus.Active || loading}
            placeholder={
              currentSession.status !== SessionStatus.Active
                ? 'Session is not active'
                : 'Type your message...'
            }
          />
        </Box>

        {/* Sidebar */}
        <Drawer
          variant="persistent"
          anchor="right"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          sx={(theme) => ({
            // Persistent drawer paper stays in layout flow; Slide only translates it off-screen,
            // so the flex row kept ~320px empty between chat and Parameters when closed.
            width: sidebarOpen ? 320 : 0,
            flexShrink: 0,
            overflow: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            '& .MuiDrawer-paper': {
              width: 320,
              position: 'relative',
              height: '100%',
              borderLeft: 1,
              borderColor: 'divider',
            },
          })}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Session Details
            </Typography>
            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <ContextUsageIndicator sessionId={currentSession.id} />
            </Box>
            <Divider sx={{ my: 3 }} />

            <List>
              <ListItem>
                <ListItemText
                  primary="Messages"
                  secondary={currentSession.messageCount}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Tokens Used"
                  secondary={currentSession.totalTokensUsed.toLocaleString()}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Total Cost"
                  secondary={`$${currentSession.totalCost.toFixed(4)}`}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Created"
                  secondary={new Date(currentSession.createdAt).toLocaleString()}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
              {currentSession.lastActivityAt && (
                <ListItem>
                  <ListItemText
                    primary="Last Activity"
                    secondary={new Date(currentSession.lastActivityAt).toLocaleString()}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItem>
              )}
            </List>

            <Divider sx={{ my: 3 }} />
            <Typography id="context-summaries" variant="subtitle2" gutterBottom fontWeight={600} component="h3">
              Chat compaction
            </Typography>
            <ContextSummaryList sessionId={currentSession.id} />

            <Divider sx={{ my: 3 }} />
            <SessionMemoryList sessionId={currentSession.id} />

            <Divider sx={{ my: 3 }} />
            <FileUpload
              sessionId={currentSession.id}
              onUploaded={() => setFilesRefreshKey((k) => k + 1)}
            />
            <FileList sessionId={currentSession.id} refreshKey={filesRefreshKey} />
          </Box>
        </Drawer>

        {parsedParameters && (
          <Box
            sx={{
              width: 'min(320px, 30vw)',
              minWidth: 260,
              flexShrink: 0,
              alignSelf: 'stretch',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'action.hover',
              borderLeft: 1,
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <TuneOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                Parameters
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                px: 2,
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {parsedParameters.map(([key, value]) => {
                const displayLabel = parameterLabelsByKey[key] ?? key;
                const valueStr =
                  typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
                return (
                <Chip
                  key={key}
                  label={`${displayLabel}: ${valueStr}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    minHeight: 32,
                    '& .MuiChip-label': {
                      whiteSpace: 'normal',
                      textAlign: 'left',
                      py: 0.75,
                    },
                  }}
                />
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
